import { FC, memo, ReactElement, SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useDateTime } from '../../../shared/useDateTime'
import { useAuth } from '../../../context/auth-context'
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, Surface, Symbols, Tooltip, XAxis, YAxis } from 'recharts'
import { Props as LegendProps } from 'recharts/types/component/DefaultLegendContent'
import { BarChartData } from '../../../modules/ownGrowth/EvaluationOverviewContainer'
import './index.less'

type Props = {
  data: BarChartData[]
  lastAppraisalDate: string
  lastAppraisalEmplPrepDate?: string
  priorAppraisalDate?: string
  privateView?: boolean
}

type XAxisCategoryTextProps = {
  label: string
  x: number
  y: number
}

export const OverviewBarChart: FC<Props> = memo(
  ({ data, privateView, lastAppraisalDate, lastAppraisalEmplPrepDate, priorAppraisalDate }) => {
    const [t] = useTranslation()
    const { user } = useAuth()
    const { formatDateMonthYearShort } = useDateTime(user)

    const showLastAppraisalBar = data.some((value) => value.last)
    const showLastAppraisalPreparationBar = data.some((value) => value.lastPreparation)

    const renderLegend = (props: LegendProps) => {
      const { payload } = props
      return (
        <ul className="legend">
          {payload &&
            payload.map(({ color, value }, index: number) => (
              <span className="legend-item" key={index}>
                <Surface width={18} height={18} viewBox={{ x: 0, y: 0, width: 10, height: 10 }} style={{ marginRight: 8 }}>
                  <Symbols cx={5} cy={5} type="square" size={100} fill={color} fillOpacity={0.5} />
                </Surface>
                <span>{value}</span>
              </span>
            ))}
        </ul>
      )
    }

    const points = [4, 7, 10, 13, 15] //checkpoints for drawing diagonal lines in the graph
    const XAxisCategoryText: FC<XAxisCategoryTextProps> = ({ label, x, y }) => {
      return (
        <text x={x} y={y - 4} textAnchor="end" style={{ fill: '#828282', fontSize: 12 }}>
          {label}
        </text>
      )
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderTick = (props: SVGProps<SVGTextElement> | ReactElement<SVGElement> | any) => {
      const { x, y, payload } = props
      const { index } = payload

      if (points.includes(index)) {
        return <path d={`M ${x + 27},342 L ${x - 148},516`} stroke="#828282" />
      }

      switch (index) {
        case 0:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.Customer`)} x={x + 30} y={y} />
        case 3:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.Speed`)} x={x + 8} y={y} />
        case 5:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.Teamwork`)} x={x + 72} y={y} />
        case 9:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.Excellence`)} x={x + 24} y={y} />
        case 12:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.Innovation`)} x={x - 6} y={y} />
        case 14:
          return <XAxisCategoryText label={t(`ownGrowthView.overview.chart.xAxisScoreCategory.HumanValues`)} x={x + 26} y={y} />
        default:
          return <span>{''}</span>
      }
    }

    const barRadius: number | [number, number, number, number] | undefined = [20, 20, 0, 0]
    const barSize = 8
    const legendType = 'square'
    const fillOpacity = 0.5

    return (
      <BarChart width={1028} height={600} data={data} barGap={2} barCategoryGap="20%">
        <CartesianGrid vertical={false} stroke="#828282" height={600} />
        <XAxis
          xAxisId={0}
          dataKey="type"
          interval={0}
          angle={-45}
          textAnchor="end"
          height={186}
          tickLine={false}
          style={{ fontSize: '12px', fontWeight: 600 }}
        />
        <XAxis xAxisId={1} dataKey="group" interval={0} height={42} axisLine={false} tickLine={false} tick={renderTick} />
        <YAxis domain={[0, 10]} tickCount={11} />
        <Tooltip separator=": " labelStyle={{ marginBottom: 8, fontWeight: 600 }} cursor={{ fill: '#ECF0F2' }} />
        <Legend wrapperStyle={{ fontSize: 12, marginBottom: 16 }} content={renderLegend} />
        <ReferenceLine y={6} stroke="#004149" />
        {priorAppraisalDate && (
          <Bar
            name={t('ownGrowthView.overview.chart.legend.appraisal', { date: formatDateMonthYearShort(priorAppraisalDate) }) as string}
            dataKey="prior"
            fill="#788187"
            fillOpacity={fillOpacity}
            radius={barRadius}
            legendType={legendType}
            barSize={barSize}
          />
        )}
        {showLastAppraisalPreparationBar && privateView && (
          <Bar
            name={
              t('ownGrowthView.overview.chart.legend.preparation', {
                date: lastAppraisalEmplPrepDate ? formatDateMonthYearShort(lastAppraisalEmplPrepDate) : '',
              }) as string
            }
            dataKey="lastPreparation"
            fill="#fcc227"
            fillOpacity={fillOpacity}
            radius={barRadius}
            legendType={legendType}
            barSize={barSize}
          />
        )}
        {showLastAppraisalBar && (
          <Bar
            name={t('ownGrowthView.overview.chart.legend.appraisal', { date: formatDateMonthYearShort(lastAppraisalDate) }) as string}
            dataKey="last"
            fill="#05cbce"
            fillOpacity={fillOpacity}
            radius={barRadius}
            legendType={legendType}
            barSize={barSize}
          />
        )}
      </BarChart>
    )
  },
)
